import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { soundEngine, sounds } from '../../lib/sounds';
import { haptics } from '../../lib/haptics';
import {
  COMMON_TIMEZONES,
  DEFAULT_TIMEZONE,
  getActiveTimezone,
  formatTimeInTz,
  formatDateInTz,
  formatDateTimeInTz,
} from '../../lib/dateUtils';
import {
  Bell,
  Check,
  Clock,
  Globe,
  Save,
  Sliders,
  Volume2,
  Shield,
  Laptop,
  Trash2,
  LogOut,
  FileClock,
  RefreshCw,
  Cpu,
  Download,
  Copy,
  Smartphone,
  Sparkles,
  Coffee,
  Lock,
  KeyRound,
  Plus,
  X,
  Utensils,
  AlertTriangle,
  Play,
  CheckCircle2,
  ChevronRight,
  SlidersHorizontal,
  Info,
  Moon,
} from 'lucide-react';
import { DOWNLOAD_LINKS } from '../../lib/downloadLinks';

interface SessionRecord {
  id: string;
  ipAddress: string;
  userAgent: string;
  lastActiveAt: string;
  createdAt: string;
  isCurrent: boolean;
}

interface AuditRecord {
  id: string;
  actorEmail: string;
  action: string;
  ipAddress: string;
  timestamp: string;
  metadata: Record<string, any>;
}

type SettingsSection = 'GENERAL' | 'INACTIVITY' | 'BREAKS' | 'ALERTS' | 'AGENT' | 'SESSIONS' | 'AUDIT';

export function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [activeSection, setActiveSection] = useState<SettingsSection>('GENERAL');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  // General & Timezone
  const [orgName, setOrgName] = useState('');
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [liveClock, setLiveClock] = useState(new Date());

  // Inactivity Radar
  const [thresholdSeconds, setThresholdSeconds] = useState(120);
  const [notifyOnResolved, setNotifyOnResolved] = useState(false);

  // Breaks & Matrix
  const [shortBreakDuration, setShortBreakDuration] = useState(10);
  const [lunchBreakDuration, setLunchBreakDuration] = useState(45);
  const [lunchBreakStart, setLunchBreakStart] = useState('13:00');
  const [lunchBreakEnd, setLunchBreakEnd] = useState('14:00');
  const [lunchBreakEnabled, setLunchBreakEnabled] = useState(true);
  const [requireBreakApproval, setRequireBreakApproval] = useState(false);
  const [customBreaks, setCustomBreaks] = useState<
    Array<{ id: string; label: string; durationMinutes: number; icon?: string; requireApproval?: boolean }>
  >([]);
  const [showAddBreakModal, setShowAddBreakModal] = useState(false);
  const [newBreakName, setNewBreakName] = useState('');
  const [newBreakDuration, setNewBreakDuration] = useState(15);
  const [newBreakIcon, setNewBreakIcon] = useState('coffee');
  const [newBreakReqApproval, setNewBreakReqApproval] = useState(false);

  // Schedule & Working Hours
  const [workHoursStart, setWorkHoursStart] = useState('09:00');
  const [workHoursEnd, setWorkHoursEnd] = useState('19:00');
  const [workDays, setWorkDays] = useState('1,2,3,4,5,6');
  const [pauseOutsideWorkHours, setPauseOutsideWorkHours] = useState(true);

  // Alerts & Sounds
  const [alertSound, setAlertSound] = useState(true);
  const [desktopNotification, setDesktopNotification] = useState(true);
  const [alertRepeatInterval, setAlertRepeatInterval] = useState(60);
  const [isPlayingTestSound, setIsPlayingTestSound] = useState(false);

  // Agent Security & Exit
  const [allowAgentExit, setAllowAgentExit] = useState(true);
  const [agentExitPin, setAgentExitPin] = useState('1234');
  const [showPin, setShowPin] = useState(false);
  const [copiedCmd, setCopiedCmd] = useState(false);

  // Sessions & Audit
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditRecord[]>([]);

  // Ticking live clock in selected timezone
  useEffect(() => {
    const timer = setInterval(() => {
      setLiveClock(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [settingsRes, sessionsRes, auditRes] = await Promise.all([
        api.admin.getSettings(),
        api.admin.getSessions(),
        api.admin.getAuditLogs(),
      ]);

      const s = settingsRes.settings;
      setOrgName(s.name || '');
      const tz = s.timezone || DEFAULT_TIMEZONE;
      setTimezone(tz);
      localStorage.setItem('pulse_tz', tz);

      setThresholdSeconds(Number(s.inactivityThresholdSeconds) || 120);
      setAlertSound(s.alertSoundEnabled ?? true);
      setDesktopNotification(s.desktopNotificationEnabled ?? true);
      setAlertRepeatInterval(Number(s.alertRepeatIntervalSeconds) || 60);

      setWorkHoursStart(s.workHoursStart || '09:00');
      setWorkHoursEnd(s.workHoursEnd || '19:00');
      setWorkDays(s.workDays || '1,2,3,4,5,6');
      setPauseOutsideWorkHours(s.pauseOutsideWorkHours !== undefined && s.pauseOutsideWorkHours !== null ? Boolean(s.pauseOutsideWorkHours) : true);

      setLunchBreakStart(s.lunchBreakStart || '13:00');
      setLunchBreakEnd(s.lunchBreakEnd || '14:00');
      setLunchBreakEnabled(s.lunchBreakEnabled !== undefined ? Boolean(s.lunchBreakEnabled) : true);
      setShortBreakDuration(Number(s.shortBreakDurationMinutes) || 10);
      setLunchBreakDuration(Number(s.lunchBreakDurationMinutes) || 45);
      setCustomBreaks(Array.isArray(s.customBreaks) ? s.customBreaks : []);
      setNotifyOnResolved(s.notifyOnResolved !== undefined ? Boolean(s.notifyOnResolved) : false);
      setRequireBreakApproval(Boolean(s.requireBreakApproval));

      setAllowAgentExit(s.allowAgentExit !== undefined ? Boolean(s.allowAgentExit) : true);
      setAgentExitPin(s.agentExitPin || '1234');

      setSessions(sessionsRes.sessions || []);
      setAuditLogs(auditRes.auditLogs || []);
    } catch (err) {
      console.error('Failed to load settings data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setSaving(true);
    setSavedSuccess(false);

    try {
      await api.admin.updateSettings({
        name: orgName,
        timezone,
        inactivityThresholdSeconds: thresholdSeconds,
        alertSoundEnabled: alertSound,
        desktopNotificationEnabled: desktopNotification,
        alertRepeatIntervalSeconds: alertRepeatInterval,
        workHoursStart,
        workHoursEnd,
        workDays,
        pauseOutsideWorkHours,
        lunchBreakStart,
        lunchBreakEnd,
        lunchBreakEnabled,
        shortBreakDurationMinutes: shortBreakDuration,
        lunchBreakDurationMinutes: lunchBreakDuration,
        customBreaks,
        notifyOnResolved,
        requireBreakApproval,
        allowAgentExit,
        agentExitPin,
      });

      // Synchronize active timezone in browser cache and AuthContext
      localStorage.setItem('pulse_tz', timezone);
      if (user?.organization) {
        user.organization.timezone = timezone;
      }
      await refreshUser();

      setSavedSuccess(true);
      soundEngine.playSuccess();
      haptics.notificationSuccess();
      setTimeout(() => setSavedSuccess(false), 3500);
    } catch (err: any) {
      soundEngine.playError();
      haptics.notificationError();
      alert(err.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSound = () => {
    setIsPlayingTestSound(true);
    soundEngine.playAlert();
    haptics.impact();
    setTimeout(() => setIsPlayingTestSound(false), 1500);
  };

  const handleTestNotification = async () => {
    if ('Notification' in window) {
      if (Notification.permission !== 'granted') {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') {
          alert('Notifications permission was denied in your browser settings.');
          return;
        }
      }
      new Notification('CK Pulse Test Notification', {
        body: `Live alert check: System time is ${formatTimeInTz(new Date(), timezone)} (${timezone}).`,
        icon: '/pulse-logo.png',
      });
      soundEngine.playChime();
    } else {
      alert('Desktop notifications are not supported in this browser.');
    }
  };

  const handleAddCustomBreak = () => {
    if (!newBreakName.trim()) return;
    const newId = `cb_${Date.now()}`;
    const updated = [
      ...customBreaks,
      {
        id: newId,
        label: newBreakName.trim(),
        durationMinutes: Number(newBreakDuration) || 15,
        icon: newBreakIcon,
        requireApproval: newBreakReqApproval,
      },
    ];
    setCustomBreaks(updated);
    setNewBreakName('');
    setNewBreakDuration(15);
    setNewBreakReqApproval(false);
    setShowAddBreakModal(false);
    haptics.selection();
    sounds.playTapSound();
  };

  const handleDeleteCustomBreak = (id: string) => {
    setCustomBreaks(customBreaks.filter((b) => b.id !== id));
    haptics.selection();
    sounds.playTapSound();
  };

  const handleRevokeSession = async (sessionId: string) => {
    if (!confirm('Are you sure you want to revoke this session?')) return;
    try {
      await api.admin.revokeSession(sessionId);
      haptics.notificationSuccess();
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err: any) {
      alert(err.message || 'Failed to revoke session');
    }
  };

  const handleRevokeOthers = async () => {
    if (!confirm('Are you sure you want to sign out all other devices?')) return;
    try {
      await api.admin.revokeOtherSessions();
      haptics.notificationSuccess();
      setSessions((prev) => prev.filter((s) => s.isCurrent));
    } catch (err: any) {
      alert(err.message || 'Failed to revoke other sessions');
    }
  };

  // Day toggle helper
  const daysList = [
    { num: '1', label: 'Mon' },
    { num: '2', label: 'Tue' },
    { num: '3', label: 'Wed' },
    { num: '4', label: 'Thu' },
    { num: '5', label: 'Fri' },
    { num: '6', label: 'Sat' },
    { num: '0', label: 'Sun' },
  ];

  const currentDaysArray = workDays.split(',').map((d) => d.trim());

  const toggleDay = (num: string) => {
    let next: string[];
    if (currentDaysArray.includes(num)) {
      if (currentDaysArray.length <= 1) return; // Keep at least 1 day
      next = currentDaysArray.filter((d) => d !== num);
    } else {
      next = [...currentDaysArray, num];
    }
    setWorkDays(next.join(','));
    haptics.selection();
  };

  const currentTzObj = COMMON_TIMEZONES.find((t) => t.value === timezone) || {
    value: timezone,
    label: timezone,
    offset: '+00:00',
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-3">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        <span className="text-xs font-mono text-zinc-500">Loading Configuration Studio...</span>
      </div>
    );
  }

  const navItems = [
    { id: 'GENERAL', label: 'General & Timezone', icon: Globe, badge: timezone.split('/')[1] || timezone },
    { id: 'INACTIVITY', label: 'Presence & Radar', icon: SlidersHorizontal, badge: `${thresholdSeconds}s` },
    { id: 'BREAKS', label: 'Break Matrix', icon: Coffee, badge: `${customBreaks.length + 2} types` },
    { id: 'ALERTS', label: 'Alerts & Audio', icon: Bell, badge: alertSound ? 'Sound ON' : 'Muted' },
    { id: 'AGENT', label: 'Workstation Agent', icon: Shield, badge: 'PIN Locked' },
    { id: 'SESSIONS', label: 'Active Sessions', icon: Laptop, badge: `${sessions.length}` },
    { id: 'AUDIT', label: 'Security Audit', icon: FileClock, badge: `${auditLogs.length}` },
  ];

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto w-full min-w-0">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-black/5 dark:border-white/5">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white tracking-tight">
              Settings & Policy Studio
            </h1>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
              v2.4
            </span>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-medium">
            Fine-tune attendance shifts, zero-keylogger idle thresholds, live timezone synchronization, and workstation rules
          </p>
        </div>

        {/* Global Save Button */}
        <div className="flex items-center gap-3">
          {savedSuccess && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 font-bold animate-in fade-in">
              <Check className="w-4 h-4" />
              <span>Policies Synchronized!</span>
            </span>
          )}
          <button
            type="button"
            onClick={() => handleSave()}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-bold rounded-xl bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/25 active:scale-[0.97] transition disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>{saving ? 'Syncing...' : 'Save Configuration'}</span>
          </button>
        </div>
      </div>

      {/* Main Two-Column Layout (Sidebar Navigation + Active Section Panel) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Navigation Menu (Desktop Sidebar & Mobile Segmented Ribbon) */}
        <div className="lg:col-span-3 space-y-1 bg-white/70 dark:bg-zinc-900/60 p-2 rounded-2xl border border-black/5 dark:border-white/5 backdrop-blur-xl shadow-sm">
          <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
            Configuration Sections
          </div>
          <div className="flex lg:flex-col overflow-x-auto gap-1 pb-1 lg:pb-0 scrollbar-none">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id as SettingsSection);
                    haptics.selection();
                    sounds.playTapSound();
                  }}
                  className={`flex-shrink-0 lg:w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition text-left ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                      : 'text-zinc-600 dark:text-zinc-400 hover:bg-black/5 dark:hover:bg-white/5 hover:text-zinc-900 dark:hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-2.5 truncate">
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </div>
                  {item.badge && (
                    <span
                      className={`hidden sm:inline-block px-1.5 py-0.5 rounded text-[10px] font-mono truncate max-w-[80px] ${
                        isActive
                          ? 'bg-white/20 text-white'
                          : 'bg-black/5 dark:bg-white/5 text-zinc-500 dark:text-zinc-400'
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Active Panel */}
        <div className="lg:col-span-9 space-y-6">
          {/* Section 1: General & Timezone */}
          {activeSection === 'GENERAL' && (
            <div className="space-y-6">
              {/* Organization Profile Card */}
              <div className="p-5 sm:p-6 rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 shadow-sm space-y-4">
                <div className="flex items-center gap-2.5 pb-3 border-b border-black/5 dark:border-white/5">
                  <div className="w-8 h-8 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-500 font-bold">
                    <Globe className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-zinc-900 dark:text-white">Organization Identity</h2>
                    <p className="text-xs text-zinc-500">Fleet naming and master operational parameters</p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                    Organization / Company Name
                  </label>
                  <input
                    type="text"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="e.g. Acme Corporation"
                    className="w-full p-3 text-xs rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white focus:outline-none focus:border-blue-500 transition font-medium"
                  />
                </div>
              </div>

              {/* Timezone Studio Card with Live Clock Confirmation */}
              <div className="p-5 sm:p-6 rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 shadow-sm space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-black/5 dark:border-white/5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-500 font-bold">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="text-sm font-bold text-zinc-900 dark:text-white">Master Timezone Engine</h2>
                      <p className="text-xs text-zinc-500">Universal time anchor for all workstations, charts, and reports</p>
                    </div>
                  </div>
                </div>

                {/* Live Digital Clock Widget */}
                <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-blue-500/10 via-indigo-500/5 to-purple-500/10 border border-blue-500/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest block">
                      Live Clock in Configured Zone
                    </span>
                    <div className="text-2xl sm:text-3xl font-mono font-black text-zinc-900 dark:text-white tracking-tight mt-1">
                      {formatTimeInTz(liveClock, timezone)}
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 flex items-center gap-2 font-medium">
                      <span>{formatDateInTz(liveClock, timezone)}</span>
                      <span>•</span>
                      <span className="text-blue-500 font-mono font-bold">{currentTzObj.label}</span>
                    </div>
                  </div>

                  <div className="flex sm:flex-col items-end justify-between sm:justify-center gap-1 border-t sm:border-t-0 sm:border-l border-blue-500/15 pt-2 sm:pt-0 sm:pl-4">
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 text-[11px] font-bold">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      <span>Active Synced</span>
                    </div>
                    <span className="text-[10px] text-zinc-400">100% of forensic logs synced</span>
                  </div>
                </div>

                {/* Timezone Selector Dropdown */}
                <div>
                  <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                    Select Primary Operational Timezone
                  </label>
                  <select
                    value={timezone}
                    onChange={(e) => {
                      const newTz = e.target.value;
                      setTimezone(newTz);
                      localStorage.setItem('pulse_tz', newTz);
                      haptics.selection();
                    }}
                    className="w-full p-3 text-xs rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white focus:outline-none focus:border-blue-500 font-mono font-semibold transition"
                  >
                    {COMMON_TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-zinc-500 mt-1.5">
                    All inactivity incidents, wallboard timelines, and PDF reports automatically render using this local timezone.
                  </p>
                </div>
              </div>

              {/* Work Shifts & Days Card */}
              <div className="p-5 sm:p-6 rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 shadow-sm space-y-4">
                <div className="flex items-center gap-2.5 pb-3 border-b border-black/5 dark:border-white/5">
                  <div className="w-8 h-8 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-500 font-bold">
                    <Clock className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-zinc-900 dark:text-white">Shift Hours & Working Days</h2>
                    <p className="text-xs text-zinc-500">Inactivity alerts only trigger during active operational shifts</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                      Shift Start Time
                    </label>
                    <input
                      type="time"
                      value={workHoursStart}
                      onChange={(e) => setWorkHoursStart(e.target.value)}
                      className="w-full p-3 text-xs rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-mono font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5">
                      Shift End Time
                    </label>
                    <input
                      type="time"
                      value={workHoursEnd}
                      onChange={(e) => setWorkHoursEnd(e.target.value)}
                      className="w-full p-3 text-xs rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-mono font-bold"
                    />
                  </div>
                </div>

                {/* Working Days Selector Pills */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300">
                      Active Operational Days
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setWorkDays('1,2,3,4,5');
                          haptics.selection();
                        }}
                        className="text-[10px] text-blue-500 hover:underline font-bold"
                      >
                        Mon–Fri
                      </button>
                      <span className="text-zinc-400">•</span>
                      <button
                        type="button"
                        onClick={() => {
                          setWorkDays('1,2,3,4,5,6');
                          haptics.selection();
                        }}
                        className="text-[10px] text-blue-500 hover:underline font-bold"
                      >
                        Mon–Sat
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-7 gap-2">
                    {daysList.map((d) => {
                      const isSelected = currentDaysArray.includes(d.num);
                      return (
                        <button
                          key={d.num}
                          type="button"
                          onClick={() => toggleDay(d.num)}
                          className={`py-2 rounded-xl text-xs font-bold transition flex flex-col items-center justify-center gap-0.5 ${
                            isSelected
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'
                          }`}
                        >
                          <span>{d.label}</span>
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              isSelected ? 'bg-white' : 'bg-transparent'
                            }`}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Pause Monitoring Outside Working Hours Toggle */}
                <div className="flex items-center justify-between p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 mt-0.5">
                      <Moon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                        <span>Pause Agent Monitoring Outside Office Hours</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono font-bold bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                          Dormant Mode
                        </span>
                      </div>
                      <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1 max-w-xl leading-relaxed">
                        Workstation agents automatically stop sending heartbeats and enter sleep mode outside your scheduled hours ({workHoursStart} – {workHoursEnd}) and non-working days. Drastically saves Cloudflare Worker edge requests and prevents false alerts.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPauseOutsideWorkHours(!pauseOutsideWorkHours);
                      haptics.selection();
                    }}
                    className={`w-12 h-6 rounded-full transition relative shrink-0 ${
                      pauseOutsideWorkHours ? 'bg-indigo-600' : 'bg-zinc-300 dark:bg-zinc-700'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full bg-white transition absolute top-0.5 shadow-sm ${
                        pauseOutsideWorkHours ? 'left-6.5' : 'left-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Section 2: Presence & Inactivity Radar */}
          {activeSection === 'INACTIVITY' && (
            <div className="space-y-6">
              <div className="p-5 sm:p-6 rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 shadow-sm space-y-5">
                <div className="flex items-center gap-2.5 pb-3 border-b border-black/5 dark:border-white/5">
                  <div className="w-8 h-8 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-500 font-bold">
                    <SlidersHorizontal className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-zinc-900 dark:text-white">Idle Detection Threshold</h2>
                    <p className="text-xs text-zinc-500">Zero-keylogging idle countdown before incident alert triggers</p>
                  </div>
                </div>

                {/* Quick Presets */}
                <div>
                  <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-2">
                    Recommended Threshold Presets
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {[
                      { secs: 60, label: '1 Min', desc: 'High Intensity' },
                      { secs: 120, label: '2 Mins', desc: 'Recommended', rec: true },
                      { secs: 180, label: '3 Mins', desc: 'Standard Office' },
                      { secs: 300, label: '5 Mins', desc: 'Relaxed' },
                    ].map((p) => {
                      const isSel = thresholdSeconds === p.secs;
                      return (
                        <button
                          key={p.secs}
                          type="button"
                          onClick={() => {
                            setThresholdSeconds(p.secs);
                            haptics.selection();
                          }}
                          className={`p-3 rounded-2xl border text-left transition flex flex-col justify-between ${
                            isSel
                              ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/25'
                              : 'bg-zinc-50 dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300 hover:border-blue-500/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-sm">{p.label}</span>
                            {p.rec && (
                              <span
                                className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                                  isSel ? 'bg-white/20 text-white' : 'bg-blue-500/15 text-blue-500'
                                }`}
                              >
                                POPULAR
                              </span>
                            )}
                          </div>
                          <span
                            className={`text-[11px] mt-1 ${isSel ? 'text-blue-100' : 'text-zinc-500'}`}
                          >
                            {p.desc} ({p.secs}s)
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Granular Slider */}
                <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-zinc-700 dark:text-zinc-300">Custom Duration Slider</span>
                    <span className="text-sm font-mono font-black text-blue-600 dark:text-blue-400">
                      {thresholdSeconds} seconds ({Math.floor(thresholdSeconds / 60)}m {thresholdSeconds % 60}s)
                    </span>
                  </div>
                  <input
                    type="range"
                    min="30"
                    max="600"
                    step="15"
                    value={thresholdSeconds}
                    onChange={(e) => setThresholdSeconds(Number(e.target.value))}
                    className="w-full accent-blue-600 cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-zinc-400 font-mono">
                    <span>30s (Ultra strict)</span>
                    <span>300s (5m)</span>
                    <span>600s (10m)</span>
                  </div>
                </div>

                {/* Manager Notification on Resume Toggle */}
                <div className="flex items-center justify-between p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800">
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-zinc-900 dark:text-white block">
                      Notify on Inactivity Resumed
                    </span>
                    <span className="text-[11px] text-zinc-500 block">
                      Send a resolution alert when the employee moves their mouse and returns to work
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setNotifyOnResolved(!notifyOnResolved);
                      haptics.selection();
                    }}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      notifyOnResolved ? 'bg-blue-600' : 'bg-zinc-300 dark:bg-zinc-700'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        notifyOnResolved ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Section 3: Break Matrix & Compliance */}
          {activeSection === 'BREAKS' && (
            <div className="space-y-6">
              <div className="p-5 sm:p-6 rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 shadow-sm space-y-5">
                <div className="flex items-center justify-between pb-3 border-b border-black/5 dark:border-white/5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-500 font-bold">
                      <Coffee className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="text-sm font-bold text-zinc-900 dark:text-white">Enterprise Break Policies</h2>
                      <p className="text-xs text-zinc-500">Preset allowance windows, lunch windows, and approval gates</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowAddBreakModal(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition shadow-sm"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Custom Break</span>
                  </button>
                </div>

                {/* Master Approval Switch */}
                <div className="flex items-center justify-between p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800">
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-zinc-900 dark:text-white block">
                      Require Manager Approval for Breaks
                    </span>
                    <span className="text-[11px] text-zinc-500 block">
                      When enabled, staff must request a break and wait for manager approval before entering break mode
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setRequireBreakApproval(!requireBreakApproval);
                      haptics.selection();
                    }}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      requireBreakApproval ? 'bg-blue-600' : 'bg-zinc-300 dark:bg-zinc-700'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        requireBreakApproval ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Default Breaks Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Short Break */}
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">☕</span>
                      <div>
                        <span className="text-xs font-bold text-zinc-900 dark:text-white block">Short Break</span>
                        <span className="text-[10px] text-zinc-500">Coffee, water, or quick rest</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400 block mb-1">
                        Duration Allowance
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="5"
                          max="30"
                          value={shortBreakDuration}
                          onChange={(e) => setShortBreakDuration(Number(e.target.value))}
                          className="w-24 p-2 text-xs rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-mono font-bold"
                        />
                        <span className="text-xs text-zinc-500 font-medium">Minutes</span>
                      </div>
                    </div>
                  </div>

                  {/* Lunch Break */}
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">🍱</span>
                      <div>
                        <span className="text-xs font-bold text-zinc-900 dark:text-white block">Lunch Break</span>
                        <span className="text-[10px] text-zinc-500">Meal & restorative midday pause</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400 block mb-1">
                        Duration Allowance
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="15"
                          max="90"
                          value={lunchBreakDuration}
                          onChange={(e) => setLunchBreakDuration(Number(e.target.value))}
                          className="w-24 p-2 text-xs rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-mono font-bold"
                        />
                        <span className="text-xs text-zinc-500 font-medium">Minutes</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Custom Break Types List */}
                <div>
                  <span className="text-xs font-bold text-zinc-700 dark:text-zinc-300 block mb-2">
                    Custom Configured Break Types ({customBreaks.length})
                  </span>
                  {customBreaks.length === 0 ? (
                    <div className="p-6 text-center rounded-2xl border border-dashed border-zinc-300 dark:border-zinc-800 text-zinc-400 text-xs">
                      No custom breaks configured yet. Click "Add Custom Break" to create department-specific breaks.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {customBreaks.map((b) => (
                        <div
                          key={b.id}
                          className="flex items-center justify-between p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800"
                        >
                          <div className="flex items-center gap-2.5">
                            <span className="text-lg">
                              {b.icon === 'utensils' ? '🍱' : b.icon === 'walk' ? '🚶' : '☕'}
                            </span>
                            <div>
                              <span className="text-xs font-bold text-zinc-800 dark:text-zinc-200 block">
                                {b.label}
                              </span>
                              <span className="text-[10px] text-zinc-400 font-mono">
                                {b.durationMinutes} mins • {b.requireApproval ? 'Requires Approval' : 'Instant'}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteCustomBreak(b.id)}
                            className="p-1.5 rounded-lg text-zinc-400 hover:text-rose-500 transition"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Section 4: Alerts & Sounds */}
          {activeSection === 'ALERTS' && (
            <div className="space-y-6">
              <div className="p-5 sm:p-6 rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 shadow-sm space-y-5">
                <div className="flex items-center gap-2.5 pb-3 border-b border-black/5 dark:border-white/5">
                  <div className="w-8 h-8 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-500 font-bold">
                    <Volume2 className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-zinc-900 dark:text-white">Audio & Desktop Alerts</h2>
                    <p className="text-xs text-zinc-500">Configure how managers and workstation users are notified</p>
                  </div>
                </div>

                {/* Audio Alert Switch + Test Button */}
                <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-zinc-900 dark:text-white block">
                      Emergency Inactivity Chime
                    </span>
                    <span className="text-[11px] text-zinc-500 block">
                      Play audible alert sound on admin dashboard and workstation when idle threshold exceeds
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleTestSound}
                      disabled={isPlayingTestSound}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 border border-purple-500/20 text-xs font-bold transition"
                    >
                      <Play className="w-3 h-3" />
                      <span>{isPlayingTestSound ? 'Playing...' : 'Test Audio'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setAlertSound(!alertSound);
                        haptics.selection();
                      }}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        alertSound ? 'bg-blue-600' : 'bg-zinc-300 dark:bg-zinc-700'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                          alertSound ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Desktop Notification Switch + Test Button */}
                <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-zinc-900 dark:text-white block">
                      Browser & OS Push Notifications
                    </span>
                    <span className="text-[11px] text-zinc-500 block">
                      Dispatch background notifications to Chrome, Edge, and Android native apps
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleTestNotification}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/20 text-xs font-bold transition"
                    >
                      <Bell className="w-3 h-3" />
                      <span>Test Push</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDesktopNotification(!desktopNotification);
                        haptics.selection();
                      }}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        desktopNotification ? 'bg-blue-600' : 'bg-zinc-300 dark:bg-zinc-700'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                          desktopNotification ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Inactivity Alert Repeat Interval */}
                <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-zinc-700 dark:text-zinc-300">
                      Alert Repeat Frequency (If Still Idle)
                    </span>
                    <span className="text-xs font-mono font-bold text-blue-500">Every {alertRepeatInterval}s</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[30, 60, 120].map((sec) => (
                      <button
                        key={sec}
                        type="button"
                        onClick={() => {
                          setAlertRepeatInterval(sec);
                          haptics.selection();
                        }}
                        className={`py-2 rounded-xl text-xs font-bold transition border ${
                          alertRepeatInterval === sec
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-zinc-800'
                        }`}
                      >
                        {sec} Seconds
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section 5: Workstation Agent Security & Downloads */}
          {activeSection === 'AGENT' && (
            <div className="space-y-6">
              {/* Agent Tamper Protection */}
              <div className="p-5 sm:p-6 rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 shadow-sm space-y-5">
                <div className="flex items-center gap-2.5 pb-3 border-b border-black/5 dark:border-white/5">
                  <div className="w-8 h-8 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-500 font-bold">
                    <Shield className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-zinc-900 dark:text-white">Agent Anti-Tamper Protection</h2>
                    <p className="text-xs text-zinc-500">Prevent unauthorized closure or unpairing of the background client</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800">
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-zinc-900 dark:text-white block">
                      Allow Employee to Exit Agent
                    </span>
                    <span className="text-[11px] text-zinc-500 block">
                      If disabled, the Windows Agent runs as a persistent service with no Exit tray option
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setAllowAgentExit(!allowAgentExit);
                      haptics.selection();
                    }}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      allowAgentExit ? 'bg-blue-600' : 'bg-zinc-300 dark:bg-zinc-700'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        allowAgentExit ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {allowAgentExit && (
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs font-bold text-zinc-900 dark:text-white block">
                          Supervisor Exit PIN Code
                        </span>
                        <span className="text-[11px] text-zinc-500">
                          Employees must enter this 4-digit code to exit or shut down the agent
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowPin(!showPin)}
                        className="text-[11px] text-blue-500 font-bold hover:underline"
                      >
                        {showPin ? 'Hide PIN' : 'Reveal PIN'}
                      </button>
                    </div>
                    <input
                      type={showPin ? 'text' : 'password'}
                      maxLength={6}
                      value={agentExitPin}
                      onChange={(e) => setAgentExitPin(e.target.value)}
                      placeholder="1234"
                      className="w-36 p-2.5 text-center text-base rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-mono font-bold tracking-widest"
                    />
                  </div>
                )}
              </div>

              {/* Downloads & Deployment Command Card */}
              <div className="p-5 sm:p-6 rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 shadow-sm space-y-4">
                <div className="flex items-center gap-2.5 pb-3 border-b border-black/5 dark:border-white/5">
                  <div className="w-8 h-8 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-500 font-bold">
                    <Download className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-zinc-900 dark:text-white">Software Downloads & Rollout</h2>
                    <p className="text-xs text-zinc-500">Official Windows agent binaries and Android manager application</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <a
                    href={DOWNLOAD_LINKS.WINDOWS_EXE}
                    download
                    className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 flex items-center justify-between hover:border-blue-500/50 transition group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-blue-500/15 text-blue-500 flex items-center justify-center font-bold">
                        <Cpu className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-zinc-900 dark:text-white block group-hover:text-blue-500 transition">
                          Windows Agent (.exe)
                        </span>
                        <span className="text-[10px] text-zinc-400">Zero-Keylogger Client v2.4</span>
                      </div>
                    </div>
                    <Download className="w-4 h-4 text-zinc-400 group-hover:text-blue-500 transition" />
                  </a>

                  <a
                    href={DOWNLOAD_LINKS.ANDROID_APK}
                    download
                    className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 flex items-center justify-between hover:border-emerald-500/50 transition group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-500/15 text-emerald-500 flex items-center justify-center font-bold">
                        <Smartphone className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-zinc-900 dark:text-white block group-hover:text-emerald-500 transition">
                          Android Manager (.apk)
                        </span>
                        <span className="text-[10px] text-zinc-400">Native Push Notifications v2.4</span>
                      </div>
                    </div>
                    <Download className="w-4 h-4 text-zinc-400 group-hover:text-emerald-500 transition" />
                  </a>
                </div>

                {/* Silent Enrollment PowerShell Snippet */}
                <div className="p-3.5 rounded-2xl bg-zinc-950 text-white font-mono text-xs space-y-2">
                  <div className="flex items-center justify-between text-[11px] text-zinc-400">
                    <span>Silent Automated IT Deployment</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `Start-Process -FilePath "CKPulse.Agent.exe" -ArgumentList "--server https://ck-pulse.pages.dev --silent" -NoNewWindow`
                        );
                        setCopiedCmd(true);
                        setTimeout(() => setCopiedCmd(false), 2000);
                      }}
                      className="text-blue-400 hover:underline flex items-center gap-1"
                    >
                      {copiedCmd ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      <span>{copiedCmd ? 'Copied!' : 'Copy Script'}</span>
                    </button>
                  </div>
                  <div className="text-zinc-300 text-[11px] truncate">
                    Start-Process -FilePath "CKPulse.Agent.exe" -ArgumentList "--server https://ck-pulse.pages.dev --silent"
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section 6: Active Sessions */}
          {activeSection === 'SESSIONS' && (
            <div className="space-y-4">
              <div className="p-5 rounded-2xl bg-white/80 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-bold text-zinc-900 dark:text-white">Active Administrator Sessions</h2>
                  <p className="text-xs text-zinc-500">Currently authenticated sessions across devices and browsers</p>
                </div>
                {sessions.filter((s) => !s.isCurrent).length > 0 && (
                  <button
                    type="button"
                    onClick={handleRevokeOthers}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 text-xs font-bold transition"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    <span>Sign Out All Other Devices</span>
                  </button>
                )}
              </div>

              <div className="rounded-2xl border border-black/5 dark:border-white/5 bg-white/80 dark:bg-zinc-900/80 overflow-hidden shadow-sm">
                <table className="w-full text-left text-xs text-zinc-700 dark:text-zinc-300">
                  <thead className="bg-zinc-100 dark:bg-zinc-950 text-zinc-600 dark:text-zinc-400 border-b border-black/5 dark:border-white/5 font-semibold">
                    <tr>
                      <th className="px-4 py-3">Device & Browser</th>
                      <th className="px-4 py-3">IP Address</th>
                      <th className="px-4 py-3">Last Active</th>
                      <th className="px-4 py-3">State</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5 dark:divide-white/5">
                    {sessions.map((s) => (
                      <tr key={s.id} className="hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition">
                        <td className="px-4 py-3 font-medium text-zinc-900 dark:text-white truncate max-w-xs">
                          {s.userAgent}
                        </td>
                        <td className="px-4 py-3 font-mono text-zinc-500">{s.ipAddress}</td>
                        <td className="px-4 py-3 font-mono text-zinc-500">
                          {formatDateTimeInTz(s.lastActiveAt, timezone)}
                        </td>
                        <td className="px-4 py-3">
                          {s.isCurrent ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-600 border border-emerald-500/30">
                              Current Device
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-400">
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {!s.isCurrent && (
                            <button
                              type="button"
                              onClick={() => handleRevokeSession(s.id)}
                              className="text-rose-500 hover:underline font-bold text-xs"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Section 7: Audit Log Trail */}
          {activeSection === 'AUDIT' && (
            <div className="space-y-4">
              <div className="p-5 rounded-2xl bg-white/80 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5">
                <h2 className="text-sm font-bold text-zinc-900 dark:text-white">Forensic Audit Log Trail</h2>
                <p className="text-xs text-zinc-500">
                  Immutable record of configuration modifications and enrollment events
                </p>
              </div>

              <div className="rounded-2xl border border-black/5 dark:border-white/5 bg-white/80 dark:bg-zinc-900/80 overflow-hidden shadow-sm">
                <table className="w-full text-left text-xs text-zinc-700 dark:text-zinc-300">
                  <thead className="bg-zinc-100 dark:bg-zinc-950 text-zinc-600 dark:text-zinc-400 border-b border-black/5 dark:border-white/5 font-semibold">
                    <tr>
                      <th className="px-4 py-3">Timestamp ({timezone})</th>
                      <th className="px-4 py-3">Actor</th>
                      <th className="px-4 py-3">Action</th>
                      <th className="px-4 py-3">IP Address</th>
                      <th className="px-4 py-3">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5 dark:divide-white/5 font-mono">
                    {auditLogs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-zinc-400 font-sans">
                          No audit events recorded yet.
                        </td>
                      </tr>
                    ) : (
                      auditLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-black/[0.02] dark:hover:bg-white/[0.02]">
                          <td className="px-4 py-3 text-zinc-500 text-[11px]">
                            {formatDateTimeInTz(log.timestamp, timezone)}
                          </td>
                          <td className="px-4 py-3 font-sans font-medium text-zinc-900 dark:text-white">
                            {log.actorEmail}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/10 text-blue-600 border border-blue-500/20">
                              {log.action}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-zinc-500 text-[11px]">{log.ipAddress}</td>
                          <td className="px-4 py-3 text-zinc-400 text-[10px] truncate max-w-xs">
                            {JSON.stringify(log.metadata)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Custom Break Modal (Rendered via Portal) */}
      {showAddBreakModal &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setShowAddBreakModal(false)}
          >
            <div
              className="bg-white dark:bg-zinc-900 border border-black/10 dark:border-white/10 rounded-3xl p-6 shadow-2xl space-y-4 max-w-md w-full relative my-auto animate-ios-enter text-left"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between pb-3 border-b border-black/5 dark:border-white/5">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-500 font-bold">
                    <Coffee className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-zinc-900 dark:text-white">New Custom Break Type</h3>
                    <p className="text-[11px] text-zinc-500">Appears directly in the Windows Agent system tray</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddBreakModal(false)}
                  className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1">Break Label</label>
                  <input
                    type="text"
                    value={newBreakName}
                    onChange={(e) => setNewBreakName(e.target.value)}
                    placeholder="e.g. Health Walk, Smoke Break, Team Sync"
                    className="w-full p-2.5 text-xs rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white focus:outline-none focus:border-blue-500 font-medium"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1">
                    Allowed Duration (Minutes)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="120"
                    value={newBreakDuration}
                    onChange={(e) => setNewBreakDuration(Number(e.target.value))}
                    className="w-full p-2.5 text-xs rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-mono font-bold"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1">Select Icon</label>
                  <div className="flex gap-2">
                    {[
                      { id: 'coffee', icon: '☕', label: 'Coffee' },
                      { id: 'utensils', icon: '🍱', label: 'Meal' },
                      { id: 'walk', icon: '🚶', label: 'Walk' },
                    ].map((ic) => (
                      <button
                        key={ic.id}
                        type="button"
                        onClick={() => setNewBreakIcon(ic.id)}
                        className={`flex-1 py-2 rounded-xl text-xs font-bold border transition flex items-center justify-center gap-1.5 ${
                          newBreakIcon === ic.id
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-zinc-50 dark:bg-zinc-950 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-zinc-800'
                        }`}
                      >
                        <span>{ic.icon}</span>
                        <span>{ic.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800">
                  <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                    Require Manager Approval
                  </span>
                  <input
                    type="checkbox"
                    checked={newBreakReqApproval}
                    onChange={(e) => setNewBreakReqApproval(e.target.checked)}
                    className="w-4 h-4 accent-blue-600 rounded"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-black/5 dark:border-white/5">
                <button
                  type="button"
                  onClick={() => setShowAddBreakModal(false)}
                  className="px-4 py-2 text-xs font-bold text-zinc-500 hover:text-zinc-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddCustomBreak}
                  disabled={!newBreakName.trim()}
                  className="px-4 py-2 text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white rounded-xl shadow-md disabled:opacity-50"
                >
                  Add Break Type
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
